import { statusOptions } from '@/common/options';
import Card from '@/components/card/card-component';
import { Col, Row } from '@/components/grid/grid-component';
import Input from '@/components/react-hook-form-inputs/input/input-component';
import PhoneInputComponent from '@/components/react-hook-form-inputs/input/phone-input/phone-input-component';
import RadioButton from '@/components/react-hook-form-inputs/radio-button/radio-button-component';
import SelectSearch from '@/components/react-hook-form-inputs/select-search/select-search-component';
import { Heading3Text } from '@/utils/global-fonts';
import {
  cellphoneNumberRegex,
  emailRegex,
  requiredValidationMessage,
} from '@/utils/validations';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { FiPlus, FiX } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid'; // Import the uuid library
import Styled from './customer-form.style';

/**
 * Customer form
 * @param { boolean }edit - form type edit or create
 * @returns {JSX.Element}
 */
const CustomerForm = ({
  isLoading: updateLoading,
  statusDisabled,
  noSpacer,
}) => {
  const [options, setOptions] = useState();
  const [customerStatus, setCustomerStatus] = useState('');
  const {
    formState: { isLoading: formLoading },
    watch,
    setValue,
    control,
    getValues,
    setFocus,
  } = useFormContext();
  const nameRef = useRef();
  const { state } = useLocation();

  const { fields, remove, insert } = useFieldArray({
    control,
    name: 'contact_person',
  });
  const {
    fields: operatingSiteFields,
    remove: removeOperatingSite,
    insert: insertOperatingSite,
  } = useFieldArray({
    control,
    name: 'operating_site',
  });
  const isLoading = formLoading || updateLoading;
  const customerStatusInitialColor = watch('customer.status');

  const validateContactPersonSuperior = (cpId) => {
    const contactPersons = getValues('contact_person')
      ?.filter((cP) => cP.id !== cpId && cP.superior !== cpId)
      .map((person) => ({ value: person.id, label: person.name }));
    return setOptions(contactPersons);
  };

  useEffect(() => {
    // When we are navigating from the quote form only,
    // we want to focus on the new name field of the contact person that we create.
    if (!state?.quoteForm) {
      return;
    }
    const fromQuoteUser = watch(`contact_person`).at(-1);
    if (fromQuoteUser) {
      const index = fields.length - 1;
      setFocus(`contact_person.${index}.name`);
    }
  }, [setFocus, setValue, watch, fields.length, state?.quoteForm]);

  return (
    <>
      <Card padding="16px 18px 10px 24px">
        <Row>
          <Col xs={12}>
            <Heading3Text> Customer Information</Heading3Text>
          </Col>
        </Row>
        <Row>
          <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
            <Input
              disabled={isLoading}
              label="Customer Name"
              placeholder="Customer Name"
              name="name"
              registerOptions={{
                required: requiredValidationMessage,
              }}
            />
          </Col>
          <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
            <Input
              disabled={isLoading}
              label="Customer Address"
              placeholder="Customer Address"
              name="address"
              registerOptions={{
                required: requiredValidationMessage,
              }}
            />
          </Col>
          <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
            <PhoneInputComponent
              name="contact_number"
              disabled={isLoading}
              onChange={() => {}}
              registerOptions={{
                validate: (value) => {
                  // TODO:Add this back after QA.I have removed this for now so that the FE triggers a required error message to accomodate the BE.
                  // if (value && value !== undefined) {
                  let errorMessage = null;
                  cellphoneNumberRegex.forEach((rule) => {
                    if (!rule.pattern.test(value)) {
                      errorMessage = rule.message;
                    }
                  });
                  return errorMessage || true;
                  // }
                  // return true;//TODO:Add this back after QA.
                },
              }}
            />
          </Col>
          <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
            <SelectSearch
              cy="selCustomerStatus"
              header="Status"
              name="status"
              label="Please select a status"
              options={statusOptions}
              isDisabled={statusDisabled}
              icon={
                <Styled.StatusCircle
                  status={customerStatusInitialColor || customerStatus}
                />
              }
              required={{
                required: 'Please select a status',
                pattern: {
                  value: /^[a-zA-Z]+$/,
                  message: 'Field cannot be empty',
                },
                onChange: (e) => {
                  setCustomerStatus(e.target.value);
                },
              }}
              isLoading={isLoading}
            />
          </Col>
        </Row>
      </Card>
      {!noSpacer && <Styled.Spacer />}
      {fields.map((item, i) => {
        const superior = getValues('contact_person')?.find(
          (x) => x.id === item.superior,
        );
        return (
          <>
            <Card padding="16px 18px 10px 24px" key={item.id}>
              <Row>
                <Col xs={12}>
                  <Row justify="space-between">
                    <Heading3Text>Customer Contact Persons</Heading3Text>
                    {fields.length > 1 && (
                      <FiX
                        size={20}
                        onClick={() => remove(i)}
                        cursor="pointer"
                      />
                    )}
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
                  <Input
                    disabled={isLoading}
                    label="Name"
                    placeholder="Contact Person Name"
                    name={`contact_person.${i}.name`}
                    registerOptions={{
                      required: requiredValidationMessage,
                      ref: nameRef,
                    }}
                  />
                </Col>
                <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
                  <Input
                    disabled={isLoading}
                    label="Email Address"
                    placeholder="Contact Person Email"
                    name={`contact_person.${i}.email`}
                    registerOptions={{
                      validate: (value) => {
                        let errorMessage = null;
                        emailRegex.forEach((rule) => {
                          if (!rule.pattern.test(value)) {
                            errorMessage = rule.message;
                          }
                        });
                        return errorMessage || true;
                      },
                    }}
                  />
                </Col>
                <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
                  <PhoneInputComponent
                    name={`contact_person.${i}.contact_number`}
                    disabled={isLoading}
                    registerOptions={{
                      validate: (value) => {
                        // TODO:Add this back after QA.I have removed this for now so that the FE triggers a required error message to accomodate the BE.
                        // if (value && value !== undefined) {
                        let errorMessage = null;
                        cellphoneNumberRegex.forEach((rule) => {
                          if (!rule.pattern.test(value)) {
                            errorMessage = rule.message;
                          }
                        });
                        return errorMessage || true;
                        // }
                        // return true;//TODO:Add this back after QA.
                      },
                    }}
                  />
                </Col>
                <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
                  <Input
                    disabled={isLoading}
                    label="Position"
                    placeholder="Company Position"
                    name={`contact_person.${i}.position`}
                    registerOptions={{
                      required: requiredValidationMessage,
                    }}
                  />
                </Col>
                <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
                  <SelectSearch
                    cy="selContactPersonSuperior"
                    header="Superior"
                    name={`contact_person.${i}.superior`}
                    label="Please select a status"
                    options={options}
                    onFocus={() =>
                      validateContactPersonSuperior(
                        getValues(`contact_person.${i}.id`),
                      )
                    }
                    icon={
                      <Styled.StatusCircle
                        status={customerStatusInitialColor || customerStatus}
                      />
                    }
                    isLoading={isLoading}
                    defaultValue={
                      superior?.id
                        ? { value: superior?.id, label: superior?.name }
                        : {}
                    }
                  />
                </Col>
                <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
                  <RadioButton
                    options={[
                      {
                        value: true,
                        label: 'Default recipient of quotes',
                      },
                    ]}
                    name={`contact_person.${i}.default_recipient_of_quotes`}
                    registerOptions={{
                      onChange: (v) => {
                        if (v.target.value === 'true') {
                          fields.forEach((_, index) => {
                            setValue(
                              `contact_person.${index}.default_recipient_of_quotes`,
                              index === i,
                            );
                          });
                        }
                      },
                    }}
                  />
                </Col>
              </Row>
              {i + 1 === fields.length && (
                <Col sm={3} md={3} lg={3} xl={3} xxl={3}>
                  <Styled.Button
                    onClick={() => {
                      insert(fields.length, {
                        id: uuidv4(),
                        name: '',
                        email: '',
                        contact_number: '',
                        position: '',
                        default_recipient_of_quotes: false,
                      });
                    }}
                  >
                    <FiPlus size={24} />
                    Add Contact Person
                  </Styled.Button>
                </Col>
              )}
            </Card>
            {!noSpacer && <Styled.Spacer />}
          </>
        );
      })}
      {operatingSiteFields.map((operatingSite, operatingSiteIndex) => (
        <>
          <Card key={operatingSite.id} padding="16px 18px 10px 24px">
            <Row>
              <Col xs={12}>
                <Row justify="space-between">
                  <Heading3Text>Customer Operating Sites</Heading3Text>

                  {operatingSiteFields.length > 1 && (
                    <FiX
                      cursor="pointer"
                      size={20}
                      onClick={() => removeOperatingSite(operatingSiteIndex)}
                    />
                  )}
                </Row>
              </Col>
            </Row>
            <Row>
              <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
                <Input
                  disabled={isLoading}
                  label="Site Name"
                  placeholder="Site Name"
                  name={`operating_site.${operatingSiteIndex}.name`}
                  registerOptions={{
                    required: requiredValidationMessage,
                  }}
                />
              </Col>
              <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
                <Input
                  disabled={isLoading}
                  label="Country"
                  placeholder="Country"
                  name={`operating_site.${operatingSiteIndex}.country`}
                  registerOptions={{
                    required: requiredValidationMessage,
                  }}
                />
              </Col>

              <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
                <Input
                  disabled={isLoading}
                  label="Nearest Town"
                  placeholder="Nearest Town"
                  name={`operating_site.${operatingSiteIndex}.nearest_town`}
                />
              </Col>
              <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
                <Input
                  disabled={isLoading}
                  label="Site Commodity"
                  placeholder="Site Commodity"
                  name={`operating_site.${operatingSiteIndex}.site_commodity`}
                />
              </Col>
              <Col sm={12} md={12} lg={12} xl={12} xxl={12}>
                <Input
                  textArea
                  disabled={isLoading}
                  label="Other"
                  placeholder=""
                  name={`operating_site.${operatingSiteIndex}.other`}
                />
              </Col>
            </Row>
            {operatingSiteIndex + 1 === operatingSiteFields.length && (
              <Col sm={3} md={3} lg={3} xl={3} xxl={3}>
                <Styled.Button
                  onClick={() => {
                    insertOperatingSite(fields.length, {
                      name: '',
                      country: '',
                      nearest_town: '',
                      site_commodity: '',
                      other: '',
                    });
                  }}
                  margin="0 0 0 0"
                >
                  <FiPlus size={24} />
                  Add Operating Site
                </Styled.Button>
              </Col>
            )}
          </Card>
          {!noSpacer && operatingSiteIndex + 1 < operatingSiteFields.length && (
            <Styled.Spacer />
          )}
        </>
      ))}
    </>
  );
};

CustomerForm.defaultProps = {
  isLoading: false,
  statusDisabled: false,
  noSpacer: false,
};

CustomerForm.propTypes = {
  isLoading: PropTypes.bool,
  statusDisabled: PropTypes.bool,
  noSpacer: PropTypes.bool,
};

export default CustomerForm;
